import * as React from "react";
import { DatasheetGridSettingsMarginType } from "@common/dto/datasheet/grid-settings-dto";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { DatasheetLocalGetDto } from "../../types/datasheet";

type Props = {
  editTabOrderActive: boolean;
  datasheet: DatasheetLocalGetDto;
  datasheetElements: Record<DatasheetElementId, DatasheetElement>;
  onDatasheetElementsChange: (datasheetElements: Record<DatasheetElementId, DatasheetElement>) => void;
};

export const useGridSettingsController = (props: Props) => {
  const { datasheet, datasheetElements, onDatasheetElementsChange, editTabOrderActive } = props;
  const [currentLatestTabOrder, setCurrentLatestTabOrder] = React.useState(0); 
  
  React.useEffect(() => {
    if (editTabOrderActive) {
      setCurrentLatestTabOrder(0);
    }
  }, [editTabOrderActive]);

  const gridSettingsMargin = React.useMemo(() => {
    const dimensions: Record<DatasheetGridSettingsMarginType, { width: number; height: number }> = {
      [DatasheetGridSettingsMarginType.fhq]: {
        width: 1100,
        height: 450,
      },
      [DatasheetGridSettingsMarginType.tablet]: {
        width: 300,
        height: 650,
      },
      [DatasheetGridSettingsMarginType.custom]: {
        width: datasheet.gridSettings.marginWidth,
        height: datasheet.gridSettings.marginHeight
      },
    };

    if (datasheet.gridSettings.marginType) {
      return {
        show: datasheet.gridSettings.showMargins,
        ...dimensions[datasheet.gridSettings.marginType],
      };
    }

    return {
      show: false,
      width: 0,
      height: 0,
    };
  }, [datasheet.gridSettings]);

  const pageBreaks = {
    show: datasheet.gridSettings.showPageBreaks,
    height: 1015,
    width: 582,
  };

  const handleDatasheetElementsTabOrderChange = React.useCallback((datasheetElement: DatasheetElement<DatasheetFieldComponentGetDto>) => {
    const datasheetElementsKeys = Object.keys(datasheetElements);
    const newTabOrder = (currentLatestTabOrder + 1) > datasheetElementsKeys.length 
      ? 1 
      : currentLatestTabOrder + 1;
    const previousTabOrder = currentLatestTabOrder;
    setCurrentLatestTabOrder(newTabOrder);

    const newElements = datasheetElementsKeys.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, id) => {
      const element = datasheetElements[id];

      if (!element) {
        return acc;
      }

      if (element.type === DatasheetElementType.text) {
        acc[id] = element;
        return acc;
      }

      const elementAsFieldComponent = element as DatasheetElement<DatasheetFieldComponentGetDto>;
      
      if (elementAsFieldComponent.id === datasheetElement.id) {
        const newElement: DatasheetElement<DatasheetFieldComponentGetDto> = {
          ...datasheetElement,
          original: {
            ...datasheetElement.original,
            taborder: newTabOrder,
          },
        };

        acc[id] = newElement;

        return acc;
      }

      let computedTabOrderForDatasheetElement = elementAsFieldComponent.original.taborder;

      if (newTabOrder === 1 && elementAsFieldComponent.original.taborder < datasheetElement.original.taborder) {
        computedTabOrderForDatasheetElement = elementAsFieldComponent.original.taborder + 1;
      }

      if (newTabOrder > 1) {
        if (elementAsFieldComponent.original.taborder < datasheetElement.original.taborder && elementAsFieldComponent.original.taborder >= newTabOrder) {
          computedTabOrderForDatasheetElement = elementAsFieldComponent.original.taborder + 1;
        } else if (elementAsFieldComponent.original.taborder <= newTabOrder && elementAsFieldComponent.original.taborder > previousTabOrder) {
          computedTabOrderForDatasheetElement = elementAsFieldComponent.original.taborder - 1;
        }
      }

      const newElement: DatasheetElement<DatasheetFieldComponentGetDto> = {
        ...elementAsFieldComponent,
        original: {
          ...elementAsFieldComponent.original,
          taborder: computedTabOrderForDatasheetElement,
        },
      };

      acc[id] = newElement;

      return acc;
    }, {});

    onDatasheetElementsChange(newElements);
  }, [onDatasheetElementsChange, datasheetElements, currentLatestTabOrder]);

  const handleResetTabOrder = React.useCallback(() => {
    const orderByYElements = Object.values(datasheetElements)
      .sort((a, b) => {
        return a.coordinates.y - b.coordinates.y;
      })
      .map((x, index) => {
        if (x.type === DatasheetElementType.text) {
          return x;
        }

        return {
          ...x,
          original: {
            ...x.original,
            taborder: index + 1
          },
        };
      })
      .reduce<Record<DatasheetElementId, DatasheetElement>>((acc, x) => {
        acc[x.id] = x;
        return acc;
      }, {});

    const newElements = {
      ...datasheetElements,
      ...orderByYElements,
    };

    onDatasheetElementsChange(newElements);
    setCurrentLatestTabOrder(0);
  }, [datasheetElements, onDatasheetElementsChange]);

  return {
    handleResetTabOrder,
    handleDatasheetElementsTabOrderChange,
    gridSettingsMargin,
    pageBreaks,
  };
};